import { Fragment } from 'react';
import BrandHeroBlock from '@/blocks/BrandHeroBlock';
import BreadcrumbsBlock from '@/blocks/BreadcrumbsBlock';
import TextImageAsideBlock from '@/blocks/TextImageAsideBlock';
import CheckList from '@/components/CheckList';
import Prose from '@/components/Prose';
import ServiceOutlineBox from '@/components/ServiceOutlineBox';
import { MEDIA_URL, REVALIDATE_TIME_IN_SEC } from '@/constants/env';
import { fadeIn, fadeInUp } from '@/constants/motions';
import { pages } from '@/content';
import FullWidthLayout from '@/layouts/FullWidthLayout';
import { motion } from 'framer-motion';
import graphql from '@/lib/graphql';
import mainLayout from '@/graphql/queries/mainLayout';
import Markdown from 'react-markdown';
import page from '@/graphql/queries/page';

function mediaUrl(image) {
  const url = image?.data?.attributes?.url;
  return url ? MEDIA_URL.concat(url) : null;
}

function BrandHeroSection({ section }) {
  const image = mediaUrl(section.heroImage);
  if (!image) return null;

  return <BrandHeroBlock image={image} title={section.heroTitle} />;
}

function TextImageAsideSection({ section, imageDirection }) {
  const image = mediaUrl(section.textImageAsideImage);
  const hasText =
    section.textImageAsideSubtitle || section.textImageAsideTitle || section.textImageAsideText;

  if (image) {
    return (
      <TextImageAsideBlock
        image={image}
        imageDirection={imageDirection}
        subtitle={section.textImageAsideSubtitle}
        title={section.textImageAsideTitle}
      >
        <Markdown>{section.textImageAsideText || ''}</Markdown>
      </TextImageAsideBlock>
    );
  }

  if (!hasText) return null;

  return (
    <motion.section
      className="my-16 px-4"
      initial="hidden"
      transition={{
        ...fadeIn.transition,
        when: 'beforeChildren',
        staggerChildren: 0.4,
      }}
      variants={fadeIn}
      viewport={fadeIn.viewport}
      whileInView="visible"
    >
      <div className="container-screen">
        {section.textImageAsideSubtitle ? (
          <motion.h4 className="text-lg text-primary-500 lg:text-xl" variants={fadeInUp}>
            {section.textImageAsideSubtitle}
          </motion.h4>
        ) : null}
        {section.textImageAsideTitle ? (
          <motion.h3 className="mt-4 text-2xl md:text-3xl xl:text-4xl" variants={fadeInUp}>
            {section.textImageAsideTitle}
          </motion.h3>
        ) : null}
        {section.textImageAsideText ? (
          <Prose className="mt-6" variants={fadeInUp}>
            <Markdown>{section.textImageAsideText}</Markdown>
          </Prose>
        ) : null}
      </div>
    </motion.section>
  );
}

function ServiceBoxesSection({ section }) {
  const services = section.services || [];
  if (!services.length) return null;

  return (
    <motion.section
      className="my-16 px-4"
      initial="hidden"
      transition={{
        ...fadeIn.transition,
        when: 'beforeChildren',
        staggerChildren: 0.4,
      }}
      variants={fadeIn}
      viewport={fadeIn.viewport}
      whileInView="visible"
    >
      <div className="container-screen">
        <motion.div
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
          transition={{
            ...fadeIn.transition,
            when: 'beforeChildren',
            staggerChildren: 0.4,
          }}
          variants={fadeIn}
        >
          {services.map(({ title, checklist }, i) => (
            <ServiceOutlineBox
              key={i}
              icon="healthicons:construction-worker"
              title={title}
              variants={fadeInUp}
            >
              <CheckList className="leading-relaxed" items={checklist || []} />
            </ServiceOutlineBox>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}

function ManpowerSection({ section, textImageIndex }) {
  switch (section?.__typename) {
    case 'ComponentBlocksBrandHero':
      return <BrandHeroSection section={section} />;
    case 'ComponentBlocksTextImageAside':
      return (
        <TextImageAsideSection
          imageDirection={textImageIndex % 2 === 0 ? 'left' : 'right'}
          section={section}
        />
      );
    case 'ComponentSectionsServiceBoxes':
      return <ServiceBoxesSection section={section} />;
    default:
      return null;
  }
}

export default function ManpowerPage({ mainLayout, page }) {
  const attributes = page?.attributes || {};
  const contentSection = attributes.contentSection || [];
  const metaData = attributes.metaData || {};
  const heroIndex = contentSection.findIndex(
    (section) => section?.__typename === 'ComponentBlocksBrandHero'
  );

  return (
    <FullWidthLayout mainLayout={mainLayout} metaData={metaData}>
      {contentSection.length === 0 ? (
        <BreadcrumbsBlock items={[pages.home, pages.manpower]} />
      ) : null}
      {contentSection.map((section, index) => {
        const textImageIndex = contentSection
          .slice(0, index)
          .filter((item) => item?.__typename === 'ComponentBlocksTextImageAside').length;

        return (
          <Fragment key={section?.id || `${section?.__typename || 'section'}-${index}`}>
            {heroIndex === -1 && index === 0 ? (
              <BreadcrumbsBlock items={[pages.home, pages.manpower]} />
            ) : null}
            <ManpowerSection section={section} textImageIndex={textImageIndex} />
            {index === heroIndex ? (
              <BreadcrumbsBlock items={[pages.home, pages.manpower]} />
            ) : null}
          </Fragment>
        );
      })}
    </FullWidthLayout>
  );
}

export async function getStaticProps() {
  const { data: mainLayoutData } = await graphql(mainLayout);

  const { data: pageData } = await graphql(page, { slug: 'manpower' });

  return {
    props: {
      mainLayout: mainLayoutData.mainLayout.data,
      page: pageData.pages.data[0],
    },
    revalidate: parseInt(REVALIDATE_TIME_IN_SEC), // In seconds
  };
}
